
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Plus } from "lucide-react";
import { AircraftTypeForm } from "./AircraftTypeForm";
import { AircraftTypeList } from "./aircraft-management/AircraftTypeList";
import { getAircraftTypes } from "@/services/aircraftService";
import { supabase } from "@/integrations/supabase/client";
import { AircraftType } from "@/types/flight";

export function AircraftTypeManagement() {
  const [aircraftTypes, setAircraftTypes] = useState<AircraftType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  

  const fetchAircraftTypes = async () => {
    try {
      const data = await getAircraftTypes();
      setAircraftTypes(data);
    } catch (error) {
      console.error('Error fetching aircraft types:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch aircraft types",
      });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchAircraftTypes();
      setLoading(false);
    };
    loadData();
  }, []);

  const handleCreateSuccess = () => {
    setIsCreateDialogOpen(false);
    fetchAircraftTypes();
  };

  const handleEditSuccess = () => {
    fetchAircraftTypes();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this aircraft type?")) return;

    try {
      const { error } = await supabase
        .from('aircraft_types')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Aircraft type deleted",
        description: "The aircraft type has been deleted successfully.",
      });
      fetchAircraftTypes();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete aircraft type",
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader style={{ backgroundColor: 'orange' }}>
          <CardTitle className="flex items-center gap-2">
            <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-5 w-5" />
            Aircraft Type Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Loading aircraft types...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center gap-2">
          <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-5 w-5" />
          Aircraft Type Management
        </CardTitle>
        <CardDescription>
          Manage aircraft types for flights
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-muted-foreground">
            {aircraftTypes.length} aircraft types total
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Aircraft Type
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Aircraft Type</DialogTitle>
              </DialogHeader>
              <AircraftTypeForm onSubmit={handleCreateSuccess} />
            </DialogContent>
          </Dialog>
        </div>

        <AircraftTypeList
          aircraftTypes={aircraftTypes}
          onEdit={handleEditSuccess}
          onDelete={handleDelete}
        />
      </CardContent>
    </Card>
  );
}
